import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate, useParams } from 'react-router';
import { useDialogs } from '../../hooks/useDialogs/useDialogs';
import useNotifications from '../../hooks/useNotifications/useNotifications';
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client/react';
import PageContainer from '../layout/PageContainer';
import { GET_VENDOR_BY_ID_QUERY, GET_VENDORS_QUERY } from '../../queries/VendorQueries';
import { DELETE_VENDOR_BY_ID_MUTATION } from '../../mutations/VendorMutations';
import type { Vendor } from '../../models/Vendor';
import { FormControlLabel, Switch, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import type { InventoryItem } from "../../models/InventoryItem";
import dayjs from "dayjs";
import { UnitType, unitTypeToUnit, unitTypeToUnits } from "../../models/Item";
import { toUSCurrency } from '../../utilities/CurrencyUtilities';
import { PurchaseOrder } from '../../models/PurchaseOrder';

export default function VendorShow() {
  const { vendorId } = useParams();
  const navigate = useNavigate();

  const dialogs = useDialogs();
  const notifications = useNotifications();

  const [getVendoryById, { refetch: refetchVendor }] = useLazyQuery(
    GET_VENDOR_BY_ID_QUERY);

  const [deleteVendorById] = useMutation(
    DELETE_VENDOR_BY_ID_MUTATION, {
    refetchQueries: [{ query: GET_VENDORS_QUERY }],
  });

  const [vendor, setVendor] = useState<Vendor | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [includeArchivedInventoryItems, setIncludeArchivedInventoryItems] = useState(false);

  const loadData = useCallback(async () => {
    setError(null);

    const id = +(vendorId || '0');

    if (id > 0) {
      setIsLoading(true);

      try {
        const { data, error: getVendorByIdError } = await getVendoryById(
          {
            variables: {
              id,
              inventoryItemsFilter: includeArchivedInventoryItems ? {} : {
                isArchived: { eq: false }
              }
            }
          });

        setIsLoading(false);
        setError(null);

        if (getVendorByIdError) {
          setError(getVendorByIdError);
        }

        if (data?.vendor) {
          setVendor(data.vendor);
        } else {
          setError({ message: 'No vendor found' } as Error);
        }
      } catch (showDataError) {
        setError(showDataError as Error);
      }
    }
    setIsLoading(false);
  }, [getVendoryById, includeArchivedInventoryItems, vendorId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleEdit = useCallback(() => {
    navigate(`/dashboard/vendors/${vendorId}/edit`);
  }, [navigate, vendorId]);

  const handleDelete = useCallback(async () => {
    if (!vendor) {
      return;
    }

    const confirmed = await dialogs.confirm(
      `Do you wish to delete ${vendor?.name}?`,
      {
        title: `Delete vendor?`,
        severity: 'error',
        okText: 'Delete',
        cancelText: 'Cancel',
      },
    );

    if (confirmed) {
      setIsLoading(true);
      try {
        const { data: result } = await deleteVendorById({ variables: { id: vendor.id } });

        if (result?.deleted) {
          navigate('/dashboard/vendors');

          notifications.show('Vendor deleted successfully.', {
            severity: 'success',
            autoHideDuration: 3000,
          });
        } else {
          notifications.show(
            'Failed to delete vendor. Reason: Unknown.',
            {
              severity: 'error',
              autoHideDuration: 3000,
            },
          );
        }
      } catch (deleteError) {
        notifications.show(
          `Failed to delete vendor. Reason:' ${(deleteError as Error).message}`,
          {
            severity: 'error',
            autoHideDuration: 3000,
          },
        );
      }
      setIsLoading(false);
    }
  }, [vendor, dialogs, deleteVendorById, navigate, notifications]);

  const handleCheckboxFieldChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setIncludeArchivedInventoryItems(event.target.checked);

      const id = +(vendorId || '0');

      refetchVendor(
        {
          id,
          inventoryItemsFilter: event.target.checked ? {} : {
            isArchived: { eq: false }
          }
        });
    },
    [refetchVendor, vendorId],
  );

  const handleBack = useCallback(() => {
    navigate('/dashboard/vendors');
  }, [navigate]);

  const renderShow = useMemo(() => {
    if (isLoading) {
      return (
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            m: 1,
          }}
        >
          <CircularProgress />
        </Box>
      );
    }
    if (error) {
      return (
        <Box sx={{ flexGrow: 1 }}>
          <Alert severity="error">{error.message}</Alert>
        </Box>
      );
    }

    return vendor ? (
      <Box sx={{ flexGrow: 1, width: '100%' }}>
        <Grid container spacing={2} sx={{ width: '100%' }}>
          <Grid size={{ xs: 10, sm: 4 }}>
            <Paper sx={{ px: 2, py: 1 }}>
              <Typography variant="overline">Name</Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                {vendor.name}
              </Typography>
            </Paper>
          </Grid>
          <Grid size={{ xs: 10, sm: 4 }}>
            <Paper sx={{ px: 2, py: 1 }}>
              <Typography variant="overline">Address</Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                {vendor.address || 'N/A'}
              </Typography>
            </Paper>
          </Grid>
          <Grid size={{ xs: 10, sm: 4 }}>
            <Paper sx={{ px: 2, py: 1 }}>
              <Typography variant="overline">City</Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                {vendor.city || 'N/A'}
              </Typography>
            </Paper>
          </Grid>
          <Grid size={{ xs: 10, sm: 4 }}>
            <Paper sx={{ px: 2, py: 1 }}>
              <Typography variant="overline">State</Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                {vendor.state || 'N/A'}
              </Typography>
            </Paper>
          </Grid>
          <Grid size={{ xs: 10, sm: 4 }}>
            <Paper sx={{ px: 2, py: 1 }}>
              <Typography variant="overline">Zip</Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                {vendor.zip || 'N/A'}
              </Typography>
            </Paper>
          </Grid>
          <Grid size={{ xs: 10, sm: 4 }}>
            <Paper sx={{ px: 2, py: 1 }}>
              <Typography variant="overline">Contact</Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                {vendor.contactId > 0 ?
                  <a href={`/dashboard/contacts/${vendor.contact?.id}`}>{vendor.contact?.name}</a> :
                  'N/A'
                }
              </Typography>
            </Paper>
          </Grid>
          <Grid size={{ xs: 12, sm: 12 }}>
            <Paper sx={{ px: 2, py: 1 }}>
              <Stack
                direction="row"
                spacing={2}
                sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  Inventory Items
                </Box>
                <Box>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={includeArchivedInventoryItems}
                        name="isIngredient"
                        onChange={handleCheckboxFieldChange}
                        size="small"
                        slotProps={{
                          input: { 'aria-label': 'controlled' },
                        }} />}
                    label="Show Archived Inventory Items" />
                </Box>
              </Stack>
              <TableContainer sx={{ maxHeight: '150px' }}>
                <Table stickyHeader sx={{ minWidth: 600 }} aria-label="dense table" size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '15%' }} component="th" scope="row" align="left">Name</TableCell>
                      <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '10%' }} component="th" scope="row" align="left">Lot #</TableCell>
                      <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '15%' }} component="th" scope="row" align="right">QOH</TableCell>
                      <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '15%' }} component="th" scope="row" align="right">Ordered Qty</TableCell>
                      <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '15%' }} component="th" scope="row" align="right">Ordered Date</TableCell>
                      <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '15%' }} component="th" scope="row" align="right">Cost per Unit</TableCell>
                      <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '15%' }} component="th" scope="row" align="right">Total Cost</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(vendor.inventoryItems || []).map((inventoryItem: InventoryItem) => (
                      <TableRow
                        key={inventoryItem.id}
                        sx={{ '&:last-child td, &:last-child th': { border: 0 }, backgroundColor: inventoryItem.isArchived ? '#fac1c0' : '' }}
                      >
                        <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '15%' }} align="left">
                          <a href={`/dashboard/items/${inventoryItem.item?.id}`}>{inventoryItem.item?.name}</a>
                        </TableCell>
                        <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '10%' }} align="left">
                          <a href={`/dashboard/inventory-items/${inventoryItem.id}`}>{inventoryItem.lotNumber || 'N/A'}</a>
                        </TableCell>
                        <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '15%' }} align="right">
                          {(inventoryItem.quantityOnHand ?? 0).toFixed(3)} {unitTypeToUnits(inventoryItem.item?.unitType as UnitType)}
                        </TableCell>
                        <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '15%' }} align="right">
                          {inventoryItem.quantityOrdered.toFixed(3)} {unitTypeToUnits(inventoryItem.item?.unitType as UnitType)}
                        </TableCell>
                        <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '15%' }} align="right">
                          <a href={`/dashboard/purchase-orders/${inventoryItem.purchaseOrderId}`}>{dayjs(inventoryItem.purchaseOrder?.dateTimePurchased).format('M/D/YYYY')}</a>
                        </TableCell>
                        <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '15%' }} align="right">
                          ${toUSCurrency(inventoryItem.totalCostPerUnit)} /{unitTypeToUnit(inventoryItem.item?.unitType as UnitType)}
                        </TableCell>
                        <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '15%' }} align="right">
                          ${toUSCurrency(inventoryItem.totalOrderedCost)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>
          <Grid size={{ xs: 12, sm: 12 }}>
            <Paper sx={{ px: 2, py: 1 }}>
              <Box>
                Purchase Orders
              </Box>
              <TableContainer sx={{ maxHeight: '150px' }}>
                <Table stickyHeader sx={{ minWidth: 600 }} aria-label="dense table" size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '50%' }} component="th" scope="row" align="left">Ordered Date</TableCell>
                      <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '50%' }} component="th" scope="row" align="right">Total Cost</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(vendor.purchaseOrders || []).map((purchaseOrder: PurchaseOrder) => (
                      <TableRow
                        key={purchaseOrder.id}
                        sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                      >
                        <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '50%' }} align="left">
                          <a href={`/dashboard/purchase-orders/${purchaseOrder.id}`}>{dayjs(purchaseOrder.dateTimePurchased).format('M/D/YYYY')}</a>
                        </TableCell>
                        <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '50%' }} align="right">
                          ${toUSCurrency(purchaseOrder.totalOrderCost)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>
        </Grid>
        <Divider sx={{ my: 3 }} />
        <Stack direction="row" spacing={2} justifyContent="space-between">
          <Button
            variant="contained"
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
          >
            Back
          </Button>
          <Stack direction="row" spacing={2}>
            <Button
              variant="contained"
              startIcon={<EditIcon />}
              onClick={handleEdit}
            >
              Edit
            </Button>
            <Button
              variant="contained"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={handleDelete}
            >
              Delete
            </Button>
          </Stack>
        </Stack>
      </Box>
    ) : null;
  }, [isLoading, error, vendor, includeArchivedInventoryItems, handleCheckboxFieldChange, handleBack, handleEdit, handleDelete]);

  const pageTitle = vendor?.name || 'N/A';

  return (
    <PageContainer
      title={pageTitle}
      breadcrumbs={[
        { title: 'Vendors', path: '/dashboard/vendors' },
        { title: pageTitle },
      ]}
    >
      <Box sx={{ display: 'flex', flex: 1, width: '100%' }}>{renderShow}</Box>
    </PageContainer>
  );
}
